import { Paper } from '@mantine/core';
import { useDisclosure, useMediaQuery, useTimeout } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { TalkingHeadActorEventIdn } from '@newo/api-client';
import { CreatorAgentSuccessFlowStepKey as AgentSuccessStep } from '@newo/tenant-metadata';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import AnimatedText from '@/components/AnimatedText';
import CheckYourPhone from '@/components/CheckYourPhone';
import { PATHS, runtimeConfig } from '@/constants';
import { useHostContext } from '@/context/HostContext.tsx';
import WaitingPlayerScreen from '@/pages/AgentTraining/components/StepsContainer/components/WaitingPlayerScreen';
import CustomerForm from '@/pages/CustomerForm';
import WaitingScreen from '@/pages/CustomerForm/components/WaitingScreen';
import { buildNavigatePath } from '@/routes.tsx';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import { partnerMetadata } from '@/services/PartnerMetadata';
import { useAgentTraining } from '@/store/agent-training';
import useAppStore from '@/store/app.ts';
import {
	AgentCreationStep,
	AgentTrainingStep,
	TScrapingStatus
} from '@/types/agent-training';
import type { CustomerData } from '@/types/common.ts';
import { cn } from '@/utils';
import { useActorSendEventMutation } from '@/utils/api.ts';
import { emailsStringToArray } from '@/utils/emails.ts';
import { playNotificationSound } from '@/utils/playNotificationSound.ts';

const StepsContainer: FC = () => {
	const { mutate: sendActorEvent } = useActorSendEventMutation();
	const [error, setError] = useState<string | undefined>('');
	const isMobile = useMediaQuery('(max-width: 768px)');

	const navigate = useNavigate();
	const {
		scrapingStatus,
		agentTrainingStep,
		setShowCustomerForm,
		setIsContactFormAllowed,
		setAgentStep,
		setTrainingStep
	} = useAgentTraining();

	const CHECK_PHONE_MODAL_ID = 'check_phone_modal';
	const isCheckPhoneOpenRef = useRef(false);
	const {
		isMiniCreatorMode,
		isOverlayMode,
		localization,
		handleAgentCreationSuccess
	} = useHostContext();

	const {
		clientSecret,
		externalId,
		connectorIdn,
		setAppState,
		setAgentData,
		setCustomerData
	} = useAppStore(
		useShallow((state) => ({
			clientSecret: state.clientSecret,
			externalId: state.externalId,
			connectorIdn: state.connectorIdn,
			setAgentData: state.setAgentData,
			setAppState: state.setAppState,
			setCustomerData: state.setCustomerData
		}))
	);

	const [isRendered, { open }] = useDisclosure(isMiniCreatorMode);

	const isOnboardingInProgress =
		agentTrainingStep === AgentTrainingStep.Loading;
	const isBlockedAnimation =
		isOnboardingInProgress ||
		scrapingStatus === TScrapingStatus.FINISHED ||
		isMiniCreatorMode;

	useTimeout(open, isBlockedAnimation ? 1000 : 20000, {
		autoInvoke: !isMiniCreatorMode
	});
	const handleCloseCheckModal = () => {
		isCheckPhoneOpenRef.current = false;
		modals.close(CHECK_PHONE_MODAL_ID);
	};

	const handleOpenCheckPhone = () => {
		if (isCheckPhoneOpenRef.current) {
			return;
		}
		playNotificationSound();
		modals.open({
			modalId: CHECK_PHONE_MODAL_ID,
			fullScreen: isMobile && !isMiniCreatorMode,
			centered: !isMobile && !isMiniCreatorMode,
			radius: isMobile || isMiniCreatorMode ? 0 : '24',
			size: isMobile || isMiniCreatorMode ? '100%' : 521,
			classNames: {
				overlay: cn({ 'absolute inset-0': isMiniCreatorMode }),
				inner: cn('!p-0', {
					'h-full absolute inset-0': isMiniCreatorMode
				}),
				content: cn({
					'h-full max-h-full w-full overflow-hidden flex flex-col':
						isMiniCreatorMode
				}),
				title: 'text-[32px] font-semibold',
				header: cn('flex items-start', {
					'!py-0 !px-2 !min-h-fit': isMiniCreatorMode
				})
			},
			title: (
				<AnimatedText
					className={cn(
						'!left-1/2 top-[45px] !-translate-x-1/2 !absolute !p-0 [font-family:var(--font-headlines)]',
						{ '!leading-[32px]': !isMiniCreatorMode }
					)}
					label={localization.translate('creator', 'call_popup.title')}
				/>
			),
			lockScroll: !isMiniCreatorMode,
			...(isMiniCreatorMode
				? {
						portalProps: {
							target: '#mini-creator-container'
						}
					}
				: {}),
			children: (
				<CheckYourPhone
					handleClose={handleCloseCheckModal}
					translate={localization.translate}
				/>
			)
		});
	};

	const createCustomer = useCallback(
		(
			data: CustomerData,
			credentials: {
				clientSecret: string;
				connectorIdn: string;
				externalId: string;
			}
		) => {
			const [email, ...linkedEmails] = emailsStringToArray(data.contact_email);
			const accountManagers = emailsStringToArray(data.account_managers);
			const allArguments = [
				{ name: 'full_name', value: data.contact_fullname?.trim() ?? '' },
				{ name: 'phone_number', value: data.contact_phone ?? '' },
				{ name: 'email', value: email ?? '' },
				{ name: 'linked_emails', value: JSON.stringify(linkedEmails) },
				{ name: 'creator_partner_idn', value: JSON.stringify(accountManagers) },
				{ name: 'account_managers', value: JSON.stringify(accountManagers) },
				{ name: 'country_code', value: data.country_code ?? '' },
				{ name: 'industry', value: data.industry ?? '' },
				{ name: 'comment', value: data.comment ?? '' },
				{
					name: 'external_logo_id',
					value: data.external_account_number ?? ''
				},
				{
					name: 'organization_logo_title',
					value: data.external_account_number ? 'Nextiva Account Number' : ''
				},
				...(data.partner_idn
					? [
							{
								name: 'partner_idn',
								value: data.partner_idn
							}
						]
					: []),
				...(data.partner_member_idn
					? [
							{
								name: 'partner_member_idn',
								value: data.partner_member_idn
							}
						]
					: [])
			];

			sendActorEvent({
				client_secret: credentials.clientSecret,
				connector_idn: credentials.connectorIdn,
				event_idn: TalkingHeadActorEventIdn.CreateCustomer,
				external_id: credentials.externalId,
				arguments: allArguments
			});
			// TODO: the contact consent step (TalkToAiAgent) is hidden in the main
			// creator flow — route back to AgentTrainingStep.ContactAgreement here and
			// in WaitingPlayerScreen.handleNext to bring it back.
			setTrainingStep(AgentTrainingStep.Loading);
		},
		[sendActorEvent, setTrainingStep]
	);

	const handleCustomerCreate = (data: CustomerData) => {
		if (!clientSecret || !connectorIdn || !externalId) {
			return;
		}

		setCustomerData({
			contact_fullname: data.contact_fullname,
			contact_email: data.contact_email,
			contact_phone: data.contact_phone,
			industry: data.industry,
			country_code: data.country_code,
			external_account_number: data.external_account_number
		});
		createCustomer(data, { clientSecret, connectorIdn, externalId });
	};

	const isCustomerScreen =
		agentTrainingStep === AgentTrainingStep.CustomerCreation;

	// biome-ignore lint/correctness/useExhaustiveDependencies: should run once
	useEffect(() => {
		const offOnboardingError = agentTrainingRealtime.onOnboardingError(
			({ description }) => {
				setError(description ?? '');
			}
		);

		const offSetCreationSettings = agentTrainingRealtime.onSetCreationSettings(
			(args) => {
				const { customer_phone, customer_email, customer_name } = args;
				setShowCustomerForm(args.show_customer_form);

				setCustomerData({
					...(customer_phone ? { contact_phone: customer_phone } : {}),
					...(customer_email ? { contact_email: customer_email } : {}),
					...(customer_name ? { contact_fullname: customer_name } : {})
				} as CustomerData);

				setIsContactFormAllowed(args.show_consent_form);
			}
		);

		const offShowCallNotification =
			agentTrainingRealtime.onShowCallNotification(handleOpenCheckPhone);

		const offOnboardingFinished = agentTrainingRealtime.onOnboardingFinished(
			(args) => {
				const firstKey =
					partnerMetadata.getAgentSuccessConfig().stepOrder?.[0] ??
					AgentSuccessStep.ActivateAccount;
				const { isAgentProgressStepsVisible } = useAgentTraining.getState();
				setAppState({
					agentSuccessActiveStep: firstKey
				});
				setAgentData(args);

				if (isAgentProgressStepsVisible && !isMiniCreatorMode) {
					return;
				}

				handleAgentCreationSuccess?.();
				if (runtimeConfig.isRemoteBuild) {
					setAgentStep(AgentCreationStep.SUCCESS);
					return;
				}
				navigate(buildNavigatePath(PATHS.agentSuccess));
			}
		);

		return () => {
			offOnboardingError();
			offOnboardingFinished();
			offSetCreationSettings();
			offShowCallNotification();
		};
	}, []);

	if (!isRendered) {
		return null;
	}

	return (
		<Paper
			className={cn(
				'!border-none !z-10 w-full !px-[20px] pt-[32px] pb-[20px]',
				'!flex !flex-col !items-center !shadow-[0_24px_48px_0_rgba(18,18,23,0.03)]',
				{
					'h-full !max-h-[auto]': isOnboardingInProgress,
					'!p-3 flex-[8] min-h-0 overflow-hidden': isMiniCreatorMode,
					'xl:!p-[60px] md:!p-[40px] !max-h-[auto] !rounded-[20px] sm:!rounded-[40px] h-full':
						!isMiniCreatorMode,
					'sm:!p-[30px] md:!p-[30px] xl:!p-[40px] !max-h-[auto] !rounded-[20px] items-stretch':
						isOverlayMode
				}
			)}
			shadow="xs"
		>
			<div
				className={cn('w-full h-full', {
					'grid-rows-[1fr]': isCustomerScreen && !isMiniCreatorMode,
					'h-full grid grid-rows-[1fr]': !isMiniCreatorMode
				})}
			>
				{agentTrainingStep === AgentTrainingStep.Onboarding && (
					<WaitingPlayerScreen />
				)}

				{agentTrainingStep === AgentTrainingStep.CustomerCreation && (
					<CustomerForm
						error={error}
						handleCustomerCreate={handleCustomerCreate}
					/>
				)}

				{agentTrainingStep === AgentTrainingStep.Loading && <WaitingScreen />}
			</div>
		</Paper>
	);
};

export default StepsContainer;
